import React from 'react';

export type NumericKeypadLeftKeyConfig = {
  label: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  ariaHidden?: boolean;
  tabIndex?: number;
  title?: string;
  dataTestId?: string;
};

export type NumericKeypadProps = {
  keypadClassName: string;
  keyClassName: string;
  disabled?: boolean;
  onDigit: (digit: string) => void;
  onBackspace: () => void;
  backspaceDisabled?: boolean;
  backspaceAriaLabel?: string;
  backspaceIcon: React.ReactNode;
  backspaceClassName?: string;
  leftKey: NumericKeypadLeftKeyConfig;
  dataTestId?: string;
};

export const NumericKeypad: React.FC<NumericKeypadProps> = ({
  keypadClassName,
  keyClassName,
  disabled = false,
  onDigit,
  onBackspace,
  backspaceDisabled = false,
  backspaceAriaLabel = 'Backspace',
  backspaceIcon,
  backspaceClassName,
  leftKey,
  dataTestId,
}) => {
  const onKeyActivate = (
    event: React.KeyboardEvent<HTMLDivElement>,
    isDisabled: boolean,
    handler?: () => void,
  ) => {
    if (isDisabled || !handler) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handler();
    }
  };

  return (
    <div className={keypadClassName} data-testid={dataTestId}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
        <div
          key={digit}
          className={keyClassName}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          data-testid={dataTestId ? `${dataTestId}-digit-${digit}` : undefined}
          onClick={disabled ? undefined : () => onDigit(String(digit))}
          onKeyDown={(event) => onKeyActivate(event, disabled, () => onDigit(String(digit)))}
        >
          {digit}
        </div>
      ))}

      <div
        className={leftKey.className ?? keyClassName}
        role="button"
        tabIndex={leftKey.tabIndex ?? ((leftKey.disabled ?? disabled) ? -1 : 0)}
        aria-disabled={leftKey.disabled ?? disabled}
        data-testid={leftKey.dataTestId ?? (dataTestId ? `${dataTestId}-left-key` : undefined)}
        onClick={(leftKey.disabled ?? disabled) ? undefined : leftKey.onClick}
        onKeyDown={(event) => onKeyActivate(event, leftKey.disabled ?? disabled, leftKey.onClick)}
        aria-label={leftKey.ariaLabel}
        aria-hidden={leftKey.ariaHidden}
        title={leftKey.title}
      >
        {leftKey.label}
      </div>

      <div
        className={keyClassName}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        data-testid={dataTestId ? `${dataTestId}-digit-0` : undefined}
        onClick={disabled ? undefined : () => onDigit('0')}
        onKeyDown={(event) => onKeyActivate(event, disabled, () => onDigit('0'))}
      >
        0
      </div>

      <div
        className={backspaceClassName ?? keyClassName}
        role="button"
        tabIndex={disabled || backspaceDisabled ? -1 : 0}
        aria-disabled={disabled || backspaceDisabled}
        data-testid={dataTestId ? `${dataTestId}-backspace` : undefined}
        onClick={disabled || backspaceDisabled ? undefined : onBackspace}
        onKeyDown={(event) => onKeyActivate(event, disabled || backspaceDisabled, onBackspace)}
        aria-label={backspaceAriaLabel}
      >
        {backspaceIcon}
      </div>
    </div>
  );
};
